import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Send, ArrowRight, Sparkles } from 'lucide-react';
import { InfluencerPostPublishCard } from '@/components/dashboard/InfluencerPostPublishCard';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useUserCredits } from '@/hooks/useUserCredits';

const copy = {
  en: {
    title: 'You Said It.',
    sub1: "Most people wait. You didn't.",
    sub2: "You just gave someone something they may never forget.",
    share: 'Share It',
    sendAnother: 'Send Another',
    returnDash: 'Return to Dashboard',
    // Upgrade nudges
    connectionNudge: 'Want to schedule moments like this automatically?',
    connectionCta: 'Explore Connection',
    legacyNudge: 'Want to add video or build a private life archive?',
    legacyCta: 'Explore Legacy',
  },
  es: {
    title: 'Lo Dijiste.',
    sub1: 'La mayoría espera. Tú no.',
    sub2: 'Acabas de darle a alguien algo que quizás nunca olvide.',
    share: 'Compartir',
    sendAnother: 'Enviar Otro',
    returnDash: 'Volver al Panel',
    connectionNudge: '¿Quieres programar momentos como este automáticamente?',
    connectionCta: 'Explorar Connection',
    legacyNudge: '¿Quieres agregar video o construir un archivo privado?',
    legacyCta: 'Explorar Legacy',
  },
  fr: {
    title: 'Vous l\'avez dit.',
    sub1: 'La plupart attendent. Pas vous.',
    sub2: 'Vous venez de donner à quelqu\'un quelque chose qu\'il n\'oubliera peut-être jamais.',
    share: 'Partager',
    sendAnother: 'Envoyer un autre',
    returnDash: 'Retour au tableau de bord',
    connectionNudge: 'Voulez-vous programmer des moments comme celui-ci automatiquement ?',
    connectionCta: 'Explorer Connection',
    legacyNudge: 'Voulez-vous ajouter de la vidéo ou créer une archive privée ?',
    legacyCta: 'Explorer Legacy',
  },
  pt: {
    title: 'Você Disse.',
    sub1: 'A maioria espera. Você não.',
    sub2: 'Você acabou de dar a alguém algo que talvez nunca esqueça.',
    share: 'Compartilhar',
    sendAnother: 'Enviar Outro',
    returnDash: 'Voltar ao Painel',
    connectionNudge: 'Quer agendar momentos como este automaticamente?',
    connectionCta: 'Explorar Connection',
    legacyNudge: 'Quer adicionar vídeo ou construir um arquivo privado?',
    legacyCta: 'Explorar Legacy',
  },
  ar: {
    title: 'لقد قلتها.',
    sub1: 'معظم الناس ينتظرون. أنت لم تنتظر.',
    sub2: 'لقد أعطيت شخصاً شيئاً قد لا ينساه أبداً.',
    share: 'مشاركة',
    sendAnother: 'إرسال آخر',
    returnDash: 'العودة للوحة التحكم',
    connectionNudge: 'تريد جدولة لحظات مثل هذه تلقائياً؟',
    connectionCta: 'استكشاف Connection',
    legacyNudge: 'تريد إضافة فيديو أو بناء أرشيف خاص؟',
    legacyCta: 'استكشاف Legacy',
  },
  ja: {
    title: '言えました。',
    sub1: 'ほとんどの人は待ちます。あなたは待ちませんでした。',
    sub2: 'あなたは誰かに忘れられないものを贈りました。',
    share: '共有する',
    sendAnother: 'もう一つ送る',
    returnDash: 'ダッシュボードに戻る',
    connectionNudge: 'このような瞬間を自動でスケジュールしたいですか？',
    connectionCta: 'Connectionを探る',
    legacyNudge: 'ビデオを追加したり、プライベートアーカイブを作りたいですか？',
    legacyCta: 'Legacyを探る',
  },
  ko: {
    title: '말했습니다.',
    sub1: '대부분의 사람들은 기다립니다. 당신은 아닙니다.',
    sub2: '누군가에게 절대 잊지 못할 것을 선물했습니다.',
    share: '공유하기',
    sendAnother: '하나 더 보내기',
    returnDash: '대시보드로 돌아가기',
    connectionNudge: '이런 순간을 자동으로 예약하고 싶으신가요?',
    connectionCta: 'Connection 탐색',
    legacyNudge: '비디오를 추가하거나 프라이빗 아카이브를 만들고 싶으신가요?',
    legacyCta: 'Legacy 탐색',
  },
  id: {
    title: 'Anda Mengatakannya.',
    sub1: 'Kebanyakan orang menunggu. Anda tidak.',
    sub2: 'Anda baru saja memberikan seseorang sesuatu yang mungkin tidak pernah mereka lupakan.',
    share: 'Bagikan',
    sendAnother: 'Kirim Lainnya',
    returnDash: 'Kembali ke Dasbor',
    connectionNudge: 'Ingin menjadwalkan momen seperti ini secara otomatis?',
    connectionCta: 'Jelajahi Connection',
    legacyNudge: 'Ingin menambahkan video atau membangun arsip pribadi?',
    legacyCta: 'Jelajahi Legacy',
  },

  hi: {
    title: 'आपने कहा।',
    sub1: "Most people wait. You didn't.",
    sub2: "You just gave someone something they may never forget.",
    share: 'इसे शेयर करें',
    sendAnother: 'एक और भेजें',
    returnDash: 'डैशबोर्ड पर लौटें',
    // Upgrade nudges
    connectionNudge: 'क्या आप ऐसे पलों को स्वचालित रूप से शेड्यूल करना चाहते हैं?',
    connectionCta: 'कनेक्शन एक्सप्लोर करें',
    legacyNudge: 'क्या आप वीडियो जोड़ना चाहते हैं या एक निजी जीवन संग्रह बनाना चाहते हैं?',
    legacyCta: 'Legacy एक्सप्लोर करें',
  },
};

// Subtle confetti particles
function ConfettiParticle({ delay, x }: { delay: number; x: number }) {
  const colors = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(45, 80%, 60%)', 'hsl(160, 40%, 55%)'];
  const color = colors[Math.floor(Math.random() * colors.length)];
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: Math.random() * 6 + 3,
        height: Math.random() * 6 + 3,
        backgroundColor: color,
        left: `${x}%`,
        top: '-5%',
      }}
      initial={{ opacity: 0, y: -20, rotate: 0 }}
      animate={{
        opacity: [0, 0.8, 0.6, 0],
        y: ['-5%', '110%'],
        x: [0, (Math.random() - 0.5) * 80],
        rotate: Math.random() * 360,
      }}
      transition={{
        duration: 2.5 + Math.random() * 1.5,
        delay: delay,
        ease: 'easeOut',
      }}
    />
  );
}

interface PostPublishReinforcementModalProps {
  open: boolean;
  onClose: () => void;
  onShare: () => void;
  recipientName?: string;
}

export function PostPublishReinforcementModal({
  open,
  onClose,
  onShare,
  recipientName,
}: PostPublishReinforcementModalProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = lc(copy, language);
  const { credits } = useUserCredits();
  const [showGlow, setShowGlow] = useState(false);
  const isDemoInvite = typeof window !== 'undefined' && sessionStorage.getItem('demo_invite') === 'true';

  const bundleType = credits?.highest_bundle_purchased;
  const isFreeOrStarter = !bundleType || bundleType === 'starter';
  const isConnection = bundleType === 'connection';

  // Background glow pulse
  useEffect(() => {
    if (open) {
      setShowGlow(true);
      const timer = setTimeout(() => setShowGlow(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Generate confetti positions
  const confettiParticles = Array.from({ length: 18 }, (_, i) => ({
    delay: Math.random() * 0.8,
    x: Math.random() * 100,
    id: i,
  }));

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-background/85 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Glow pulse */}
        {showGlow && (
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.15, 0] }}
            transition={{ duration: 2.5, ease: 'easeInOut' }}
            style={{
              background: 'radial-gradient(circle at center, hsl(var(--primary) / 0.3), transparent 70%)',
            }}
          />
        )}

        {/* Confetti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confettiParticles.map((p) => (
            <ConfettiParticle key={p.id} delay={p.delay} x={p.x} />
          ))}
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 24 }}
          transition={{ type: 'spring', damping: 22, stiffness: 260 }}
          className="relative z-10 bg-card rounded-3xl shadow-2xl border border-border p-8 md:p-10 max-w-md w-full text-center"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Heart icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 400, damping: 15 }}
            className="inline-flex p-5 rounded-full bg-primary/10 mb-6"
          >
            <Heart className="h-12 w-12 text-primary" fill="currentColor" />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3"
          >
            {t.title}
          </motion.h2>

          {/* Subtexts */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-lg text-muted-foreground mb-2"
          >
            {t.sub1}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="text-base text-muted-foreground/80 mb-8"
          >
            {t.sub2}
          </motion.p>

          {/* Influencer referral link card — only for demo invite users */}
          {isDemoInvite && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="mb-6"
            >
              <InfluencerPostPublishCard />
            </motion.div>
          )}

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="space-y-3"
          >
            <Button
              onClick={() => {
                onClose();
                navigate('/create');
              }}
              className="w-full gap-2"
              size="lg"
            >
              <ArrowRight className="h-4 w-4" />
              {t.sendAnother}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => {
                onClose();
                navigate('/dashboard#puzzle');
              }}
            >
              {t.returnDash}
            </Button>
          </motion.div>

          {/* Contextual upgrade nudge */}
          {(isFreeOrStarter || isConnection) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75 }}
              className="mt-8 pt-6 border-t border-border/50"
            >
              <p className="text-sm text-muted-foreground mb-3">
                {isFreeOrStarter ? t.connectionNudge : t.legacyNudge}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-primary border-primary/30 hover:bg-primary/5"
                onClick={() => {
                  onClose();
                  navigate('/#pricing');
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
                {isFreeOrStarter ? t.connectionCta : t.legacyCta}
              </Button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
